import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  confirmationFooter,
  DISCLOSURE_PLACEHOLDERS,
  MAX_DISCLOSURE_CHARS,
  MAX_DISCLOSURE_PARAGRAPHS,
  operatorAccountHolderName,
  organisationChanges,
  statementDisclosures,
  validateOrganisation,
  type OrganisationField,
  type OrganisationSettings,
} from '@norvix/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ORGANISATION_KEY, organisationApi, type StoredOrganisation } from '../../api/organisation.api.js';
import { settingsHistoryKey, type SettingsHistoryEntry } from '../../api/settings-history.api.js';
import { organisationRestoreApi } from '../../api/settings-restore.api.js';

import { SettingsHistoryButton, useSettingsLastChanged } from './SettingsHistory.js';
import { RestoreSaveBar } from './SettingsRestore.js';
import { useSectionRestore } from './useSettingsAccess.js';
import { SettingsSaveBar } from './SettingsSaveBar.js';
import { SettingsSectionHeader } from './SettingsSectionHeader.js';
import type { PendingChange } from './settings-changes.js';

/**
 * Settings → Organisation → Company details (DEV-138, DEV-143).
 *
 * Who Norvix is, typed once: the statement PDF, the payment confirmation, the
 * commission transfer's recipient name and the customer security notice all
 * read it. Admin only. Every change needs a reason, kept in the History,
 * because it changes what customers' documents say.
 */

/** The shortest reason the API accepts (organisation-settings.ts). */
const MIN_REASON = 10;

interface Draft {
  readonly legalName: string;
  readonly registrationNumber: string;
  /** One address line per row. */
  readonly address: string;
  readonly paragraphs: readonly string[];
  readonly baseCurrency: string;
  readonly reportingCurrency: string;
  readonly timeZone: string;
  readonly paymentCutoff: string;
  readonly supportEmail: string;
}

function toDraft(org: OrganisationSettings): Draft {
  const paragraphs = [...org.statementDisclosures];
  while (paragraphs.length < MAX_DISCLOSURE_PARAGRAPHS) paragraphs.push('');
  return {
    legalName: org.legalName,
    registrationNumber: org.registrationNumber,
    address: org.addressLines.join('\n'),
    paragraphs,
    baseCurrency: org.baseCurrency,
    reportingCurrency: org.reportingCurrency,
    timeZone: org.timeZone,
    paymentCutoff: org.paymentCutoff,
    supportEmail: org.supportEmail,
  };
}

/** The draft as the API would store it, trimmed the same way, valid or not. */
function toSettings(draft: Draft): OrganisationSettings {
  const lines = (values: readonly string[]): string[] => values.map((v) => v.trim()).filter((v) => v !== '');
  return {
    legalName: draft.legalName.trim(),
    registrationNumber: draft.registrationNumber.trim(),
    addressLines: lines(draft.address.split('\n')),
    statementDisclosures: lines(draft.paragraphs),
    baseCurrency: draft.baseCurrency.trim().toUpperCase(),
    reportingCurrency: draft.reportingCurrency.trim().toUpperCase(),
    timeZone: draft.timeZone.trim(),
    paymentCutoff: draft.paymentCutoff.trim(),
    supportEmail: draft.supportEmail.trim().toLowerCase(),
  };
}

function Card({ title, children, help }: { title: string; help?: string; children: ReactNode }): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
      {help !== undefined ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{help}</Typography> : <Box sx={{ mb: 2 }} />}
      <Stack spacing={2}>{children}</Stack>
    </Paper>
  );
}

interface FormProps {
  readonly stored: StoredOrganisation;
  /** DEV-142: the version "Restore this version" was pressed on, until it is restored or cancelled. */
  readonly restoreVersion: number | null;
  readonly onRestoreDone: () => void;
}

/**
 * DEV-142: restoring a version through the save bar. The values shown are
 * the server's dry run against what is stored; what is saved is the stored
 * version, read by the server. Unsaved edits must be saved or discarded first.
 */
function OrganisationRestore({ stored, version, editing, onDone, onRestored }: {
  stored: StoredOrganisation; version: number; editing: boolean; onDone: () => void; onRestored: (saved: StoredOrganisation, version: number) => void;
}): JSX.Element | null {
  const preview = useQuery({
    queryKey: ['organisation-restore-preview', version, stored.version],
    queryFn: () => organisationRestoreApi.preview(version, stored.version),
    retry: false,
  });
  const restore = useMutation({
    mutationFn: (reason: string) => organisationRestoreApi.restore({
      version, expectedVersion: stored.version, reason,
      ...(preview.data?.commissionRecipient !== undefined ? { confirmCommissionRecipientChange: true } : {}),
    }),
    onSuccess: (saved) => onRestored(saved, version),
  });
  if (editing) {
    return <Alert severity="warning" sx={{ mt: 2 }} action={<Button onClick={onDone}>Cancel restore</Button>}>
      Save or discard your edits before restoring version {String(version)}.
    </Alert>;
  }
  if (preview.isLoading) return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (preview.isError || preview.data === undefined) {
    return <Alert severity="error" sx={{ mt: 2 }} action={<Button onClick={onDone}>Close</Button>}>
      {preview.error instanceof Error ? preview.error.message : `Version ${String(version)} cannot be restored.`}
    </Alert>;
  }
  const recipient = preview.data.commissionRecipient;
  return (
    <RestoreSaveBar
      plan={{
        label: `version ${String(version)}`,
        changes: [
          ...preview.data.changes,
          ...(recipient === undefined ? [] : [{ key: 'commissionRecipient', label: 'Commission transfer recipient (Verification of Payee)', before: recipient.before, after: recipient.after }]),
        ],
        ...(recipient === undefined ? {} : {
          warning: `This also changes the recipient name on the daily commission transfer, which the bank checks with Verification of Payee: ${recipient.before} → ${recipient.after}. It must match the name the revenue account is held under. Restoring confirms it.`,
        }),
      }}
      saving={restore.isPending}
      {...(restore.isError ? { error: restore.error instanceof Error ? restore.error.message : 'The version could not be restored.' } : {})}
      onConfirm={(reason) => restore.mutate(reason)}
      onCancel={onDone}
    />
  );
}

function OrganisationForm({ stored, restoreVersion, onRestoreDone }: FormProps): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(stored.value));
  const [savedAt, setSavedAt] = useState(0);
  const [notice, setNotice] = useState('');
  const candidate = toSettings(draft);
  const checked = validateOrganisation(candidate);
  const errors: Readonly<Partial<Record<OrganisationField, string>>> = checked.ok ? {} : checked.errors;
  // A legal-name edit re-addresses the commission transfer when GuruPay does
  // not name the account holder itself; Verification of Payee compares it.
  const recipient = stored.commissionRecipient;
  const recipientAfter = operatorAccountHolderName(candidate);
  const recipientChanges = recipient?.followsLegalName === true && recipientAfter !== recipient.name;
  const changes: PendingChange[] = [
    ...organisationChanges(stored.value, checked.ok ? checked.value : candidate),
    ...(recipientChanges ? [{ key: 'commissionRecipient', label: 'Commission transfer recipient (Verification of Payee)', before: recipient.name, after: recipientAfter }] : []),
  ].map((change) => ({ ...change, secret: false, needsReason: true }));
  const recipientWarning = recipientChanges
    ? `This also changes the recipient name on the daily commission transfer, which the bank checks with Verification of Payee: ${recipient.name} → ${recipientAfter}. It must match the name the revenue account is held under. Saving confirms it.`
    : undefined;
  const rendered = statementDisclosures(candidate);
  const renderedLength = rendered.join('').length;

  const save = useMutation({
    onMutate: () => setNotice(''),
    mutationFn: (reason: string | undefined) => {
      if (!checked.ok) throw new Error('Correct the highlighted fields first.');
      return organisationApi.save({ expectedVersion: stored.version, value: checked.value, reason: reason ?? '',
        ...(recipientChanges ? { confirmCommissionRecipientChange: true } : {}) });
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(ORGANISATION_KEY, saved);
      setDraft(toDraft(saved.value));
      setSavedAt(Date.now());
      setNotice('Saved. New statements, payment confirmations and security notices use it from now on.');
      await queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ORGANISATION') });
    },
  });

  const set = (patch: Partial<Draft>): void => {
    setNotice('');
    setDraft((current) => ({ ...current, ...patch }));
  };
  const field = (key: OrganisationField, help?: string): { error: boolean; helperText?: string } => {
    const message = errors[key];
    return message !== undefined ? { error: true, helperText: message } : help !== undefined ? { error: false, helperText: help } : { error: false };
  };
  const placeholderHelp = `Use ${DISCLOSURE_PLACEHOLDERS.join(', ')} for the details above, so they are typed once.`;

  return (
    <Box>
      {notice !== '' ? <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert> : null}
      {stored.commissionRecipientUnavailable !== undefined ? <Alert severity="warning" sx={{ mb: 2 }}>{stored.commissionRecipientUnavailable}</Alert> : null}

      <Card title="Legal entity" help="The company that operates Norvix, exactly as registered. The commission transfer is addressed to this name in capitals, as the bank holds our accounts.">
        <TextField label="Legal name" value={draft.legalName} onChange={(e) => set({ legalName: e.target.value })} {...field('legalName')} />
        <TextField label="Registration number" value={draft.registrationNumber} onChange={(e) => set({ registrationNumber: e.target.value })} {...field('registrationNumber')} />
        <TextField
          label="Registered address"
          multiline
          minRows={3}
          value={draft.address}
          onChange={(e) => set({ address: e.target.value })}
          {...field('addressLines', 'One line per row. Printed on one line, separated by commas.')}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }} aria-label="Payment confirmation footer preview">
          Payment confirmation footer: {'\n'}{confirmationFooter(candidate)}
        </Typography>
      </Card>

      <Card title="Regulatory disclosures" help={`Printed under "Operator and regulatory information" at the foot of every statement page. ${placeholderHelp}`}>
        {draft.paragraphs.map((paragraph, index) => (
          <TextField
            key={index}
            label={`Paragraph ${String(index + 1)}${index === 0 ? '' : ' (optional)'}`}
            multiline
            minRows={2}
            value={paragraph}
            onChange={(e) => set({ paragraphs: draft.paragraphs.map((p, i) => (i === index ? e.target.value.replace(/\n/g, ' ') : p)) })}
            error={index === 0 && errors.statementDisclosures !== undefined}
            {...(index === 0 && errors.statementDisclosures !== undefined ? { helperText: errors.statementDisclosures } : {})}
          />
        ))}
        <Box>
          <Typography variant="caption" color={renderedLength > MAX_DISCLOSURE_CHARS ? 'error' : 'text.secondary'}>
            As printed: {String(renderedLength)} of {String(MAX_DISCLOSURE_CHARS)} characters
          </Typography>
          {rendered.map((text, index) => (
            <Typography key={index} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{text}</Typography>
          ))}
        </Box>
      </Card>

      <Card title="Currencies" help="Recorded for the platform. Nothing converts amounts into them yet; the platform runs in EUR.">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="Base currency" value={draft.baseCurrency} onChange={(e) => set({ baseCurrency: e.target.value })} {...field('baseCurrency', 'ISO 4217, e.g. EUR')} />
          <TextField label="Reporting currency" value={draft.reportingCurrency} onChange={(e) => set({ reportingCurrency: e.target.value })} {...field('reportingCurrency', 'ISO 4217, e.g. EUR')} />
        </Stack>
      </Card>

      <Card title="Time zone and cut-off" help="Recorded here; schedules and document dates still run on UTC.">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="Time zone" value={draft.timeZone} onChange={(e) => set({ timeZone: e.target.value })} {...field('timeZone', 'IANA, e.g. UTC or Europe/Vilnius')} />
          <TextField label="Payment cut-off (optional)" placeholder="HH:MM" value={draft.paymentCutoff} onChange={(e) => set({ paymentCutoff: e.target.value })} {...field('paymentCutoff', '24-hour, in this time zone')} />
        </Stack>
      </Card>

      <Card title="Support contact" help="Given to customers in every account-security notice. Email only.">
        <TextField label="Support email" type="email" value={draft.supportEmail} onChange={(e) => set({ supportEmail: e.target.value })} {...field('supportEmail')} />
      </Card>

      {restoreVersion !== null ? (
        <OrganisationRestore
          stored={stored}
          version={restoreVersion}
          editing={changes.length > 0}
          onDone={onRestoreDone}
          onRestored={(saved, version) => {
            queryClient.setQueryData(ORGANISATION_KEY, saved);
            setDraft(toDraft(saved.value));
            setNotice(`Version ${String(version)} restored as version ${String(saved.version)}. New statements, payment confirmations and security notices use it from now on.`);
            onRestoreDone();
            void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ORGANISATION') });
          }}
        />
      ) : null}

      <SettingsSaveBar
        sticky
        changes={changes}
        saving={save.isPending}
        onSave={(reason) => save.mutate(reason)}
        onDiscard={() => { setDraft(toDraft(stored.value)); setNotice(''); }}
        reasonMinLength={MIN_REASON}
        reasonPrompt="These details are printed on customers' statements, confirmations and security notices, so the reason is kept in the History."
        savedAt={savedAt}
        {...(recipientWarning === undefined ? {} : { warning: recipientWarning })}
        {...(checked.ok ? {} : { blocked: 'Correct the highlighted fields before saving.' })}
        {...(save.isError ? { error: save.error instanceof Error ? save.error.message : 'The organisation details could not be saved.' } : {})}
      />
    </Box>
  );
}

export function OrganisationSettingsPage(): JSX.Element {
  const lastChanged = useSettingsLastChanged('ORGANISATION');
  const stored = useQuery({ queryKey: ORGANISATION_KEY, queryFn: () => organisationApi.get() });
  // DEV-142: restore is declared per section in the shared registry.
  const restore = useSectionRestore({ group: 'organisation', id: 'company' });
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);

  return (
    <Box>
      <SettingsSectionHeader
        title="Company details"
        lastChanged={lastChanged}
        actions={<SettingsHistoryButton subject="ORGANISATION" title="Company details"
          {...(restore.canRestore ? { onRestore: (entry: SettingsHistoryEntry) => setRestoreVersion(Number(entry.restoreTarget)) } : {})} />}
      >
        Who Norvix is, typed once. Statements, payment confirmations, the commission transfer and customer
        security notices all read it from here. Every change asks for a reason and is kept in the History.
      </SettingsSectionHeader>
      {stored.isLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : null}
      {stored.isError ? (
        <Alert severity="error">{stored.error instanceof Error ? stored.error.message : 'Could not load the organisation details.'}</Alert>
      ) : null}
      {stored.data !== undefined
        ? <OrganisationForm stored={stored.data} restoreVersion={restoreVersion} onRestoreDone={() => setRestoreVersion(null)} />
        : null}
    </Box>
  );
}
